// eslint-disable-next-line @typescript-eslint/naming-convention
import React from 'react';
import { api } from '../app/api/baseApi';
import { useGetWidgetDetails } from './useGetWidgetDetails';
import { customEndpointSelector, isWidgetApplicable, isWidgetConfigured } from '../utils/helpers';
import { useLazyGetDashboardWidgetByIdQuery as useLazyGetWidgetByIdQuery } from '../features/widgetManagement/widgetApi';
import { useGetAllFeatureFlagQuery } from '../features/featureFlag/featureFlagApi';
import { useAppDispatch, useTypedSelector } from '../app/store';
import {
    resetDashboard,
    setSummaryResponse,
    setSummaryWidgetDetailsLoader,
    removeSummaryWidgetLoader,
    setWidgetList,
} from '../features/dashboardManagement/commonDashboardSlice';
import { useGetDeviceByIdQuery } from '../features/deviceManagement/deviceApi';
import { FeatureFlagData } from '../types';

type ReturnProps = {
    isLoading: boolean;
    refreshValues: () => Promise<void>;
};

const ENDPOINTS_WITH_SUFFIX = [
    'getDeviceAlertCount',
    'getLoadGroupTrends',
    'getAvailableCommands',
    'getAggregatedBarChartData',
    'getEnvironmentalStressMonitoringMetrics',
    'getEnvironmentalStressMonitoringFilter',
];

const buildWidgetValueKey = (widget: any, endpoint: any): string => {
    const suffix = ENDPOINTS_WITH_SUFFIX.includes(endpoint?.endpoint) ? `-${endpoint?.endpoint}` : '';
    return `${widget?.widgetType?.id}${suffix}`;
};

// For gauge widgets filtered by instance profile, channelId can be null.
// Skip getChannelDetails in that case to avoid unnecessary API calls.
const isGaugeChannelSkippable = (widget: any, endpoint: any, selectedWidgetConfig: any): boolean =>
    widget?.widgetType?.id === 'gauge' &&
    endpoint?.endpoint === 'getChannelDetails' &&
    (selectedWidgetConfig?.data?.channelId === null || selectedWidgetConfig?.data?.channelId === undefined);

export const useCommonWidgetConfiguration = (dashboardData: any, dataGetter: any): ReturnProps => {
    const dispatch = useAppDispatch();
    const pollingRefs = React.useRef<{ [key: string]: any }>({});
    const hasFetchedValuesRef = React.useRef(false);
    const processedResponseRefs = React.useRef<Record<string, any>>({});
    const shouldGateWidgetQueryDeviceIdByOntologyFlag =
        dataGetter?.shouldGateWidgetQueryDeviceIdByOntologyFlag === true;
    const widgets = useTypedSelector((rootState) => rootState.commonDashboard.widgetList);
    const loadingState = useTypedSelector((rootState) => rootState.commonDashboard.loaders);
    const apiResponses = useTypedSelector((rootState) => rootState.commonDashboard.apiResponse);
    const { data: featureFlagData } = useGetAllFeatureFlagQuery(
        {},
        {
            skip: !shouldGateWidgetQueryDeviceIdByOntologyFlag,
        }
    );
    const isOntologyActivationEnabled = React.useMemo(
        () =>
            featureFlagData?.data?.some(
                (featureFlag: FeatureFlagData) =>
                    typeof featureFlag?.featureFlagName === 'string' &&
                    featureFlag?.featureFlagName?.toLowerCase() === 'instance-ontology-support' &&
                    featureFlag?.enabled === true
            ) ?? false,
        [featureFlagData]
    );
    const widgetQueryDeviceId = shouldGateWidgetQueryDeviceIdByOntologyFlag
        ? isOntologyActivationEnabled
            ? dataGetter?.deviceId
            : undefined
        : dataGetter?.widgetQueryDeviceId;
    const widgetIdListKey = React.useMemo(
        () => dashboardData?.widgetIdList?.join(',') ?? '',
        [dashboardData?.widgetIdList]
    );
    const dashboardContextKey = React.useMemo(
        () =>
            [dashboardData?.id ?? '', widgetIdListKey, dataGetter['deviceId'] ?? '', dataGetter['groupId'] ?? ''].join(
                '|'
            ),
        [dashboardData?.id, widgetIdListKey, dataGetter['deviceId'], dataGetter['groupId']]
    );
    const failedPollingApis = apiResponses.filter(
        (failApi: { type: string; responses: Array<{ isError: any }> }) =>
            failApi.type === 'energy_flow' && failApi.responses[0].isError
    );
    const { currentData: deviceData }: any = useGetDeviceByIdQuery(dataGetter['deviceId'], {
        skip: !dataGetter['deviceId'],
    });
    const [getWidgetConfig, { isFetching, isLoading }] = useLazyGetWidgetByIdQuery();

    const isWidgetLoading = React.useCallback(
        (widgetId: any): boolean => loadingState?.[widgetId] === 'loading',
        [loadingState]
    );

    const { payload, response: widgetResponse } = useGetWidgetDetails({
        selectorType: 'commonDashboard',
        dataGetter,
    });

    function widgetConfigHandler(widgetId: any): void {
        dispatch(setSummaryWidgetDetailsLoader({ key: widgetId, value: 'loading' }));
        const widgetArgs = widgetQueryDeviceId ? { widgetId, deviceId: widgetQueryDeviceId } : widgetId;

        getWidgetConfig(widgetArgs)
            .then((res: any) => {
                const actualWidgetId = res?.data?.data?.id || widgetId;
                dispatch(setWidgetList({ data: res?.data?.data, widgetId: widgetId }));
                dispatch(removeSummaryWidgetLoader(widgetId));
                dispatch(removeSummaryWidgetLoader(actualWidgetId));
            })
            .catch((err: any) => {
                console.error(err);
                dispatch(removeSummaryWidgetLoader(widgetId));
            });
    }

    React.useEffect(() => {
        hasFetchedValuesRef.current = false;
        processedResponseRefs.current = {};
        dispatch(resetDashboard());
    }, [dispatch, dashboardContextKey]);

    React.useEffect(() => {
        if (dashboardData?.widgetIdList?.length) {
            dashboardData?.widgetIdList?.forEach((widgetId: any) => {
                widgetConfigHandler(widgetId);
            });
        }
    }, [widgetIdListKey, widgetQueryDeviceId]);

    React.useEffect(() => {
        // Don't process API responses while the widget list is still loading.
        // A stale or aborted response can arrive when widgetsClone is empty (after a
        // resetDashboard). If we mark it processed here, the entry is skipped when
        // widgets reload — leaving the widget permanently blank. Deferring until
        // widgets are populated ensures every response is applied to real widget state.
        if (!widgets?.length) {
            return;
        }

        if (apiResponses?.length) {
            apiResponses.forEach((responseEntry: any) => {
                const { responses, type, widgetId } = responseEntry;

                if (isWidgetLoading(widgetId) || processedResponseRefs.current[widgetId] === responseEntry) {
                    return;
                }

                processedResponseRefs.current[widgetId] = responseEntry;
                (widgetResponse[type] || widgetResponse['default'])?.(responses, widgetId);
            });
        }
    }, [apiResponses, loadingState, widgetResponse, widgets]);

    React.useEffect(() => {
        if (
            !hasFetchedValuesRef.current &&
            widgets?.length === dashboardData?.widgetIdList?.length &&
            widgets?.every((item: any) => !isWidgetLoading(item?.id))
        ) {
            hasFetchedValuesRef.current = true;
            void getValues();
        }
    }, [widgets, widgetIdListKey, loadingState, dataGetter]);

    async function setWidgetValue(widget: any, endpoint: any): Promise<any> {
        const selectedWidget = widgets?.find((prevWidget: any) => prevWidget?.id === widget?.id);
        const selectedWidgetConfig = selectedWidget?.config;

        if (isGaugeChannelSkippable(widget, endpoint, selectedWidgetConfig)) {
            return { data: { data: [] } };
        }

        const key = buildWidgetValueKey(widget, endpoint);
        const payloadDummy = await (payload?.[key] || payload?.['default'])?.(
            selectedWidgetConfig,
            selectedWidget?.selectedEntities
        );

        // If payload is undefined, skip this API call
        if (payloadDummy === undefined) {
            return Promise.resolve({ data: undefined });
        }

        const endpointInitiate = (api as any)?.endpoints?.[endpoint?.endpoint]?.initiate;
        if (!endpointInitiate) {
            return Promise.resolve({ data: undefined });
        }

        return new Promise((resolve) => {
            dispatch(endpointInitiate(payloadDummy, { forceRefetch: true })).then((response: any) => {
                resolve(response);
            });
        });
    }

    function getWidgetApplicableType(widget: any): any {
        if (dataGetter['deviceId']) {
            if (deviceData?.data?.wrapperDeviceType === 'Gateway' && widget?.widgetType?.id === 'device_list') {
                return 'GROUP';
            }
            return 'DEVICE';
        }
        return 'GROUP';
    }

    async function getValues(): Promise<void> {
        await Promise.all(
            (widgets ?? []).map(async (widget: any) => {
                if (
                    widget?.widgetType?.internalCdnJsLink ||
                    !isWidgetApplicable(widget, getWidgetApplicableType(widget)) ||
                    !isWidgetConfigured(widget)
                ) {
                    dispatch(setSummaryWidgetDetailsLoader({ key: widget?.id, value: 'loaded' }));
                } else if (!isWidgetLoading(widget?.id)) {
                    const promises: any = [];

                    for (let i = 0; i < customEndpointSelector(widget?.widgetType?.id)?.length; i++) {
                        const endpoint = customEndpointSelector(widget?.widgetType?.id, widget?.config?.type)[i];
                        promises.push(setWidgetValue(widget, endpoint));
                    }

                    await Promise.all(promises)
                        .then((responses) => {
                            dispatch(setSummaryResponse({ widget, responses }));
                        })
                        .catch((e) => {
                            console.error(e);
                        });
                }
            })
        );
    }

    // Effect to stop specific polling if any APIs fail
    React.useEffect(() => {
        if (failedPollingApis.length) {
            failedPollingApis.forEach((failApi: { widgetId: string | number }) => {
                clearInterval(pollingRefs.current[failApi.widgetId]);
            });
        }
    }, [failedPollingApis]);

    return {
        isLoading: isLoading || isFetching,
        refreshValues: getValues,
    };
};
